import * as React from "react";
import { Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

function format12(value) {
  if (!value || !/^\d{1,2}:\d{2}$/.test(value)) return "";
  const [hStr, mStr] = value.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (isNaN(h) || isNaN(m)) return "";
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

function parseInput(input, opts = {}) {
  if (!input) return "";
  let cleaned = input.trim().toUpperCase().replace(/\./g, "").replace(/\s+/g, "");

  let period = null;
  const periodMatch = cleaned.match(/(AM|PM|A|P)$/);
  if (periodMatch) {
    period = periodMatch[1];
    cleaned = cleaned.slice(0, -periodMatch[1].length);
    if (period === "A") period = "AM";
    if (period === "P") period = "PM";
  }
  const hadExplicitPeriod = !!period;

  let hour;
  let minute;

  if (cleaned.includes(":")) {
    const parts = cleaned.split(":");
    if (parts.length !== 2 || !/^\d{1,2}$/.test(parts[0]) || !/^\d{1,2}$/.test(parts[1])) {
      return null;
    }
    hour = parseInt(parts[0], 10);
    minute = parseInt(parts[1].padEnd(2, "0"), 10);
  } else {
    if (!/^\d+$/.test(cleaned)) return null;
    if (cleaned.length <= 2) {
      hour = parseInt(cleaned, 10);
      minute = 0;
    } else if (cleaned.length === 3) {
      hour = parseInt(cleaned.slice(0, 1), 10);
      minute = parseInt(cleaned.slice(1), 10);
    } else if (cleaned.length === 4) {
      hour = parseInt(cleaned.slice(0, 2), 10);
      minute = parseInt(cleaned.slice(2), 10);
    } else {
      return null;
    }
  }

  if (isNaN(hour) || isNaN(minute) || minute < 0 || minute > 59 || hour < 0) return null;

  if (period) {
    if (hour < 1 || hour > 12) return null;
    if (period === "AM" && hour === 12) hour = 0;
    else if (period === "PM" && hour !== 12) hour += 12;
  } else {
    if (hour > 23) return null;
  }

  // Context-aware AM/PM inference for ambiguous input (no explicit period, 12h-range).
  if (!hadExplicitPeriod) {
    if (opts.prefer === "after" && opts.referenceTime && /^\d{1,2}:\d{2}$/.test(opts.referenceTime)) {
      const [rh, rm] = opts.referenceTime.split(":").map(Number);
      const refMin = rh * 60 + rm;
      const curMin = hour * 60 + minute;
      if (curMin <= refMin && hour < 12) hour += 12;
    } else if (opts.prefer === "before" && opts.referenceTime && /^\d{1,2}:\d{2}$/.test(opts.referenceTime)) {
      const [rh, rm] = opts.referenceTime.split(":").map(Number);
      const refMin = rh * 60 + rm;
      const curMin = hour * 60 + minute;
      if (curMin >= refMin && hour >= 12 && hour < 24) hour -= 12;
    } else if (opts.prefer === "businessHours") {
      // Hours 1-6 → afternoon (PM), 7-12 → morning (AM). Skip 0 (midnight) and 13+ (explicit 24h).
      if (hour >= 1 && hour <= 6) hour += 12;
    }
  }

  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

export function TimePicker({
  id,
  value,
  onChange,
  className,
  placeholder = "e.g. 2:30 PM",
  referenceTime,
  prefer,
  error = false,
}) {
  const [text, setText] = React.useState(format12(value));

  React.useEffect(() => {
    setText(format12(value));
  }, [value]);

  const commit = (input) => {
    if (!input.trim()) {
      onChange("");
      setText("");
      return;
    }
    const parsed = parseInput(input, { referenceTime, prefer });
    if (parsed !== null) {
      onChange(parsed);
      setText(format12(parsed));
    } else {
      setText(format12(value));
    }
  };

  return (
    <div className={cn("relative", className)}>
      <Clock
        className={cn(
          "pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2",
          error ? "text-destructive" : "text-muted-foreground"
        )}
      />
      <Input
        id={id}
        type="text"
        inputMode="text"
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => commit(text)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit(text);
          }
        }}
        className={cn(
          "pl-9",
          error && "border-destructive focus-visible:ring-destructive"
        )}
        aria-invalid={error || undefined}
      />
    </div>
  );
}
